// src/encounter/lib/to-condition-request.ts
// Build the wire bodies for the IPS-rail Active/Resolved Problems sections. REUSES the
// curl-verified CreateConditionBody / UpdateConditionBody contracts (patient-chart-api.ts,
// locked 2026-05-19) + the conditions.ts helpers (clientCreatedDate, emptyBodySite,
// normalizeConditionCode, buildResolveDateFields). Pure — mirrors the Flags + Allergy mappers.
//
// Actions:
//  - add     → POST /api/Patients/CreateCondition (CreateConditionBody; clinicalStatus:'Active').
//  - edit    → PUT  /api/Patients/condition/{id}  (UpdateConditionBody; status UNCHANGED).
//  - status  → PUT  /api/Patients/condition/{id}  (active↔resolved = clinicalStatus on update;
//              Resolve populates endDate + endDisplayDate; Reactivate clears them).
//  - delete  → PUT  /api/Patients/condition/{id}  (isDeleted:true; preserve everything else).
//
// ⚠️ GROUNDING NOTE (PROD-1257 lesson): the Flutter `PatientConditions.toJson()` emits
// `bodySite: null` when null, but the curl-verified backend REJECTS null bodySite and requires
// the empty-Coding sentinel. We follow the curl-verified type (emptyBodySite), NOT toJson.
// The fields flagged UNCERTAIN in the report (createdDate client-set, performer sentinel) are
// carried verbatim from the curl-verified type docblocks but should be live-smoke-validated.
import {
  CONDITION_STATUS,
  type ConditionCode,
  type CreateConditionBody,
  type PatientCondition,
  type UpdateConditionBody,
} from '@/types/patient-chart-api';
import type { CodeWordCase } from '@/types/patient-api';
import {
  buildResolveDateFields,
  clientCreatedDate,
  emptyBodySite,
  normalizeConditionCode,
} from '@/patients/lib/conditions';
import { normalizeCoding } from './to-allergy-request';
import { appendAttributedNote } from './rail-note-piece';

/**
 * Severity is a bare STRING on Conditions (not a Coding). The problem form no longer collects it:
 * the encounter owner ruling (1 Nov 2026, "chart rail + form fields") drops Severity from the
 * Problem form. Create sends the wire's neutral 'n/a'; update round-trips whatever the record
 * already holds so an edit never rewrites a stored value through a field the form does not show.
 */
const CONDITION_SEVERITY_NONE = 'n/a';

export interface ConditionRequestContext {
  /**
   * The encounter this item is being recorded IN — stamped on CREATE as the wire `encounter`.
   *
   * FLUTTER PARITY. Its shared clinical-info popup applies `data.encounter = encounterId` to EVERY
   * summary type before dispatching per type (`popup_widget.dart:152`), and the per-tab add paths do
   * the same (`allergies_tab.dart:785`, `history_tab.dart:1318,1490` → `encounter:
   * widget.encounterId`). Nearly every patient model carries the field — allergies, immunization,
   * conditions, procedures, medications, labs, flags, medical + social history.
   *
   * PA sent a hardcoded `encounter: null` here, so an item added on the pre-encounter had no record
   * of which visit produced it. Andrew, 2026-08-28: "when i call any api in the pre-encounter for
   * allergy, vaccine, family history, social history,.... we should add encounterId value."
   *
   * Optional on purpose. These builders are ENCOUNTER-ONLY — the patients-module chart has its own
   * path (`patients/services/use-create-allergy.ts` etc.), so an earlier version of this comment
   * claiming they "also serve the chart" was wrong. The reason it stays optional is that the
   * encounter itself can lack an id: a deep-link with no `createContext`, a Booked appointment
   * (where the open-phase create is deliberately gated), or an open-phase create that failed. In
   * those the rail still renders and still writes. Absent → `null`, exactly the previous behaviour.
   */
  encounterId?: string | null;
  patientId: string;
  /** Current user's userId GUID — stamped as `asserter` on CREATE. */
  userId: string;
  /** Current user's display name — stamped on the CREATE performer sentinel. */
  userName?: string | null;
}

/** The dialog's resolved selection. */
export interface ConditionFormValue {
  /** Selected diagnosis code (ICD-10 master row). */
  code: ConditionCode;
  /** "Active" | "Resolved" — bucket the row lands in. */
  clinicalStatus: 'Active' | 'Resolved';
  /** ISO 8601 no-Z onset. Null when unknown. */
  onsetDateTime: string | null;
  /** User's free-text onset display. */
  onsetDisplayDateTime: string | null;
  note?: string | null;
  /** Resolution date as `YYYY-MM-DD` (from a date input) — required only when
   *  clinicalStatus === 'Resolved'. Builds endDate + endDisplayDate. */
  endYyyymmdd?: string | null;
}

export type ConditionAction =
  | { type: 'add'; value: ConditionFormValue }
  | { type: 'edit'; existing: PatientCondition; value: ConditionFormValue }
  | { type: 'delete'; existing: PatientCondition }
  // One-click Resolve (BRD row action, PROD-1489): flip an Active condition to Resolved with a
  // resolution date, round-tripping the ORIGINAL record (no form-value reconstruction → no field
  // drift). Same UPDATE path as an edit-to-Resolved, but without opening the dialog.
  | { type: 'resolve'; existing: PatientCondition; endYyyymmdd: string }
  // One-click Unresolve (Flutter row action, `problems_tab.dart:325-332`, string `S.of(context).unresolve`;
  // also on the encounter rail as `AppConstants.actionUnResolveProblem`, `clinical_info_widget.dart:397`).
  // Flutter mutates the record in place — `clinicalStatus = Active`, `onsetDateTime = now` — and posts
  // the whole object, so it NEVER touches endDate: the resolution date is PRESERVED. That differs from
  // the dialog-driven `edit`-to-Active path, which clears both end fields and is pinned by
  // "Reactivate (Resolved→Active) clears endDate/endDisplayDate". Both behaviours are kept as-is:
  // this action matches Flutter's dedicated button, the edit action keeps its guarded behaviour.
  // The inconsistency between them is real and flagged for review rather than silently unified.
  | { type: 'unresolve'; existing: PatientCondition }
  /** Undo resolve (ruling 3, "it was a mistake"): back to Active exactly as before the resolve — the stored onset kept, the
   *  resolution dates cleared. Unlike Unresolve ("Reactivate", Flutter parity), the onset is NOT stamped to now. */
  | { type: 'undo-resolve'; existing: PatientCondition }
  // "+ Note" (BRD row action, PROD-1489): write the condition's single scalar note, preserving
  // every other field. SINGLE-note semantics per Flutter (patient_conditions.dart `note`) and the
  // existing BE contract — the dialog prefills the current note so saving edits-in-place rather
  // than silently clobbering. Append-only Condition.note[] history is NOT the model here (Tariq,
  // PROD-1489 — the "append/timestamped" reading was never in Samer's spec).
  | { type: 'note'; existing: PatientCondition; note: string };

// The slim performer sentinel Conditions Create ships (curl-verified). id null, name = editing
// user, speciality.specialitySkillId = all-zero GUID. ⚠️ flagged UNCERTAIN for live-smoke.
function createPerformer(userName: string | null | undefined): CreateConditionBody['performerDetails'] {
  return {
    id: null,
    name: userName ?? null,
    photo: null,
    speciality: {
      specialitySkillId: '00000000-0000-0000-0000-000000000000',
      code: null,
    },
    timeZone: null,
    organizationId: null,
    roleName: null,
  };
}

function normalizePerformerDetails(
  pd: PatientCondition['performerDetails'],
): UpdateConditionBody['performerDetails'] {
  if (!pd) return null;
  return {
    id: pd.id ?? null,
    name: pd.name ?? null,
    photo: pd.photo ?? null,
    speciality: pd.speciality
      ? {
          specialitySkillId: pd.speciality.specialitySkillId ?? null,
          code: pd.speciality.code
            ? {
                code: pd.speciality.code.code ?? null,
                displayName: pd.speciality.code.displayName ?? null,
                system: pd.speciality.code.system ?? null,
                description: pd.speciality.code.description ?? null,
                shortName: pd.speciality.code.shortName ?? null,
              }
            : null,
        }
      : null,
    timeZone: pd.timeZone ?? null,
    organizationId: pd.organizationId ?? null,
    roleName: pd.roleName ?? null,
  };
}

/** CREATE body. clinicalStatus always 'Active' on create (a new problem starts active);
 *  if the dialog opens in the Resolved bucket the row is created Active then resolved via
 *  a follow-up update — matching the chart-tab flow (Resolve is a separate PUT). */
export function buildCreateConditionBody(
  value: ConditionFormValue,
  ctx: ConditionRequestContext,
): CreateConditionBody {
  return {
    identifier: null,
    clinicalStatus: 'Active',
    code: normalizeConditionCode(value.code),
    // Empty Coding (NOT null) — backend rejects null bodySite on create.
    bodySite: emptyBodySite(null),
    subject: ctx.patientId,
    dataAbsentReason: 'n/a',
    encounter: ctx.encounterId ?? null,
    asserter: ctx.userId,
    endDisplayDate: null,
    onsetDisplayDateTime: value.onsetDisplayDateTime,
    onsetDateTime: value.onsetDateTime,
    endDate: null,
    note: value.note?.trim() || '',
    severity: CONDITION_SEVERITY_NONE,
    isDeleted: false,
    performerDetails: createPerformer(ctx.userName),
    // ⚠️ CLIENT-SET timestamp (PHI caveat, PROD-837). Backend assumed to overwrite; can't omit.
    createdDate: clientCreatedDate(),
    speciality: null,
  };
}

/** LOCAL calendar now, in the two shapes the wire wants. Local, not UTC — `toISOString()` records
 *  the wrong clinical day near midnight in +offset zones (same trap the Resolve path documents). */
function pad2(n: number): string {
  return String(n).padStart(2, '0');
}
function nowIsoNoZ(): string {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}.000`;
}
function nowDisplay(): string {
  const d = new Date();
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;
}

/** Shared EDIT/STATUS/DELETE body — round-trips the original record. */
function buildUpdateConditionBody(
  existing: PatientCondition,
  value: ConditionFormValue | undefined,
  opts: {
    clinicalStatus: 'Active' | 'Resolved';
    isDeleted: boolean;
    endYyyymmdd?: string | null;
    /** Overrides `note` while `value` is undefined (the '+ Note' row action). */
    noteOverride?: string;
    /** Unresolve: stamp onset to NOW, as Flutter does (`data.onsetDateTime = DateTime.now()`). */
    onsetNow?: boolean;
    /** Unresolve: keep the ORIGINAL end dates instead of nulling them for an Active status.
     *  Flutter's Unresolve button leaves endDate untouched; only the dialog edit clears it. */
    preserveEndDates?: boolean;
  },
  ctx: ConditionRequestContext,
): UpdateConditionBody {
  // Resolve-with-date → build endDate + endDisplayDate from the given day. A Resolved record
  // updated WITHOUT a new date (soft-delete, note) round-trips its ORIGINAL end dates — nulling
  // them here silently erased the resolution date on delete/note of a resolved row. Active
  // (edit/reactivate) → null both.
  const resolved =
    opts.clinicalStatus === CONDITION_STATUS.resolved
      ? opts.endYyyymmdd
        ? buildResolveDateFields(opts.endYyyymmdd)
        : { endDate: existing.endDate ?? null, endDisplayDate: existing.endDisplayDate ?? null }
      : opts.preserveEndDates
        ? { endDate: existing.endDate ?? null, endDisplayDate: existing.endDisplayDate ?? null }
        : null;
  return {
    identifier: existing.identifier!,
    clinicalStatus: opts.clinicalStatus,
    code: value ? normalizeConditionCode(value.code) : normalizeConditionCode(existing.code),
    bodySite: emptyBodySite(existing.bodySite),
    subject: ctx.patientId,
    dataAbsentReason: 'n/a',
    /**
     * The CURRENT encounter wins, falling back to the record's own.
     *
     * FLUTTER PARITY (Andrew, 2026-08-28: "we will do as flutter"). Its shared popup assigns
     * `data.encounter = encounterId` BEFORE branching on `identifier != null`
     * (`popup_widget.dart:152`), so an edit re-stamps the item with the encounter it is being
     * edited IN — not the one that first recorded it. An earlier pass here preserved
     * `existing.encounter` on the reasoning that editing an old allergy shouldn't re-attribute it;
     * that was my call, and Andrew overruled it. It also fixes items PA wrote before the create-path
     * fix: they carry `encounter: null` and the next edit now heals them, where round-tripping would
     * have kept them null forever.
     *
     * THE `?? existing.encounter` IS NOT DECORATION. Flutter can assign unconditionally because its
     * popup guarantees an id first (`if (isBlank(encounterId)) createEncounter()`, initState). We
     * have no such guarantee: a deep-link with no `createContext`, a Booked appointment (open-phase
     * create gated), or a failed open-phase create all leave `encounterId` null while the rail still
     * renders and still writes. Assigning unconditionally in those cases would write null over a
     * real link and DESTROY the record's provenance. So: re-stamp when we have an encounter,
     * preserve when we don't.
     *
     * (An earlier version of this note justified the fallback with the patients-module chart. That
     * was wrong — the chart has its own request path and never calls these builders.)
     */
    encounter: ctx.encounterId ?? existing.encounter ?? null,
    // Preserve the ORIGINAL asserter (recording user), not the editor.
    asserter: existing.asserter ?? null,
    endDisplayDate: resolved ? resolved.endDisplayDate : null,
    onsetDisplayDateTime: value
      ? value.onsetDisplayDateTime
      : opts.onsetNow
        ? nowDisplay()
        : existing.onsetDisplayDateTime ?? null,
    onsetDateTime: value
      ? value.onsetDateTime
      : opts.onsetNow
        ? nowIsoNoZ()
        : existing.onsetDateTime ?? null,
    endDate: resolved ? resolved.endDate : null,
    note: value
      ? value.note?.trim() || ''
      : opts.noteOverride !== undefined
        ? opts.noteOverride.trim()
        : existing.note ?? '',
    // Never collected by the form (ruling above): every path round-trips the stored value.
    severity: existing.severity ?? CONDITION_SEVERITY_NONE,
    isDeleted: opts.isDeleted,
    performerDetails: normalizePerformerDetails(existing.performerDetails),
    createdDate: existing.createdDate ?? null,
    // Top-level speciality IS round-tripped on Conditions UPDATE (differs from Allergies).
    speciality: normalizeCoding(existing.speciality as Partial<CodeWordCase> | null),
  };
}

export interface ConditionRequest {
  kind: 'create' | 'update' | 'entered-in-error';
  /** Condition GUID for update path (`PUT /condition/{id}`) and the entered-in-error transition; undefined on create. */
  conditionId?: string;
  body: CreateConditionBody | UpdateConditionBody | ConditionEnteredInErrorBody;
}

/** `ConditionEnteredInErrorRequest { Reason }` (Scrips.Patient T4, Application/Dtos/PatientChart/ConditionTransitionRequests.cs). */
export interface ConditionEnteredInErrorBody {
  reason: string;
}

/**
 * "Entered in error" on a problem (left-rail target §3; clinical ruling 1 Nov: a separate action from Resolve, a REQUIRED reason,
 * the record leaves active display, stays in history and is never deleted). Scrips.Patient origin/master:
 * POST /api/Patients/condition/{conditionId}/entered-in-error (PatientsController `MarkConditionEnteredInError`, PROD-1682).
 * Null when there is no record id or no reason: the request is never sent without one.
 */
export function buildConditionEnteredInErrorRequest(conditionId: string | null | undefined, reason: string): ConditionRequest | null {
  const r = reason.trim();
  if (!conditionId || !r) return null;
  return { kind: 'entered-in-error', conditionId, body: { reason: r } };
}

/**
 * Pre-visit "Add a note" on a problem (left-rail target §4): the record's note with ONE line APPENDED, "<text> — <author>, <D-45
 * date>" (`appendAttributedNote`), through the same `note` update. The stored text is kept exactly; never a replacement. Null when
 * there is nothing to add. Server-side note history is not here (Andrew's lane): the single note field carries the lines.
 */
export function buildConditionAppendNoteRequest(
  existing: PatientCondition,
  text: string,
  author: { name: string | null | undefined; atIso: string },
  ctx: ConditionRequestContext,
): ConditionRequest | null {
  if (!text.trim()) return null;
  const note = appendAttributedNote(existing.note, text, author.name, author.atIso);
  return buildConditionRequest({ type: 'note', existing, note }, ctx);
}

/**
 * Resolve a condition action into endpoint + body.
 *  - add  → CREATE (always Active).
 *  - edit → UPDATE; if the form's clinicalStatus differs from the record's, this also performs
 *           the active↔resolved transition (Resolve requires `endYyyymmdd`).
 *  - delete → UPDATE soft-delete.
 * Returns null when the action is structurally invalid (edit/delete without identifier, or a
 * Resolve transition without a resolution date).
 */
export function buildConditionRequest(
  action: ConditionAction,
  ctx: ConditionRequestContext,
): ConditionRequest | null {
  if (action.type === 'add') {
    return { kind: 'create', body: buildCreateConditionBody(action.value, ctx) };
  }
  if (!action.existing.identifier) return null;

  if (action.type === 'delete') {
    // Soft-delete preserves the record's current clinicalStatus.
    const status: 'Active' | 'Resolved' =
      action.existing.clinicalStatus === CONDITION_STATUS.resolved ? 'Resolved' : 'Active';
    return {
      kind: 'update',
      conditionId: action.existing.identifier,
      body: buildUpdateConditionBody(action.existing, undefined, { clinicalStatus: status, isDeleted: true }, ctx),
    };
  }

  if (action.type === 'note') {
    // Preserve the record's current status (and, via buildUpdateConditionBody, its end dates);
    // only the scalar note changes.
    const status: 'Active' | 'Resolved' =
      action.existing.clinicalStatus === CONDITION_STATUS.resolved ? 'Resolved' : 'Active';
    return {
      kind: 'update',
      conditionId: action.existing.identifier,
      body: buildUpdateConditionBody(
        action.existing,
        undefined,
        { clinicalStatus: status, isDeleted: false, noteOverride: action.note },
        ctx,
      ),
    };
  }

  if (action.type === 'resolve') {
    if (!action.endYyyymmdd) return null; // Resolve requires a resolution date.
    // value: undefined → buildUpdateConditionBody keeps every original field (code/onset/note/
    // severity/asserter) and only flips clinicalStatus → Resolved + sets endDate from endYyyymmdd.
    return {
      kind: 'update',
      conditionId: action.existing.identifier,
      body: buildUpdateConditionBody(
        action.existing,
        undefined,
        { clinicalStatus: 'Resolved', isDeleted: false, endYyyymmdd: action.endYyyymmdd },
        ctx,
      ),
    };
  }

  if (action.type === 'undo-resolve') {
    if (action.existing.clinicalStatus !== CONDITION_STATUS.resolved) return null;
    return {
      kind: 'update',
      conditionId: action.existing.identifier,
      body: buildUpdateConditionBody(action.existing, undefined, { clinicalStatus: 'Active', isDeleted: false }, ctx),
    };
  }

  if (action.type === 'unresolve') {
    if (!action.existing.identifier) return null;
    // Defence-in-depth: only a Resolved row can be unresolved (mirrors the chart tab's guard).
    if (action.existing.clinicalStatus !== CONDITION_STATUS.resolved) return null;
    return {
      kind: 'update',
      conditionId: action.existing.identifier,
      body: buildUpdateConditionBody(
        action.existing,
        undefined,
        { clinicalStatus: 'Active', isDeleted: false, onsetNow: true, preserveEndDates: true },
        ctx,
      ),
    };
  }

  // edit (may also transition status)
  const target = action.value.clinicalStatus;
  if (target === CONDITION_STATUS.resolved && !action.value.endYyyymmdd) {
    // Resolve requires a resolution date — block instead of shipping an inconsistent body.
    return null;
  }
  return {
    kind: 'update',
    conditionId: action.existing.identifier,
    body: buildUpdateConditionBody(
      action.existing,
      action.value,
      { clinicalStatus: target, isDeleted: false, endYyyymmdd: action.value.endYyyymmdd },
      ctx,
    ),
  };
}
